// Tremor Raw Button [v0.0.0]
import { Slot } from "@radix-ui/react-slot"
import RiLoader2Fill from "~/icons/remix/generated/RiLoader2Fill"
import { type ComponentPropsWithoutRef, forwardRef } from "react"
import { type VariantProps, tv } from "tailwind-variants"

import { cx, focusRing } from "~/lib/utils"

const buttonVariants = tv({
  base: [
    // base
    "relative inline-flex items-center justify-center rounded-lg border px-3 py-1.5 text-center text-sm font-medium shadow-sm transition-all duration-100 ease-in-out",
    // disabled
    "disabled:pointer-events-none disabled:shadow-none",
    // focus
    focusRing
  ],
  variants: {
    variant: {
      primary: [
        // border
        "border-indigo-700 dark:border-indigo-800",
        // text color
        "text-white dark:text-white",
        // background color
        "bg-indigo-800 dark:bg-indigo-600",
        // added hover color
        "hover:bg-gradient-to-r from-rose-800 to-indigo-800",
        // disabled
        "disabled:bg-indigo-100 disabled:text-indigo-400",
        "disabled:dark:bg-indigo-800 disabled:dark:text-indigo-600"
      ],
      secondary: [
        // border
        "border-gray-300 dark:border-gray-800",
        // text color
        "text-gray-900 dark:text-gray-50",
        // background color
        "bg-white dark:bg-gray-950",
        //hover color
        "hover:bg-gray-50 dark:hover:bg-gray-900/60",
        // disabled
        "disabled:text-gray-400",
        "disabled:dark:text-gray-600"
      ],
      ghost: [
        // border
        "border-transparent",
        // text color
        "text-gray-600 dark:text-gray-300",
        // background color
        "bg-transparent",
        // hover color
        "hover:bg-gray-100 dark:hover:bg-gray-800",
        // disabled
        "disabled:text-gray-400 dark:disabled:text-gray-600",
        "disabled:bg-transparent dark:disabled:bg-transparent"
      ],
      light: [
        // base
        "shadow-none",
        // border
        "border-transparent",
        // text color
        "text-gray-900 dark:text-gray-50",
        // background color
        "bg-gray-200 dark:bg-gray-900",
        // hover color
        "hover:bg-gray-300/70 dark:hover:bg-gray-800/80",
        // disabled
        "disabled:bg-gray-100 disabled:text-gray-400",
        "disabled:dark:bg-gray-800 disabled:dark:text-gray-600"
      ],
      destructive: [
        // text color
        "text-white",
        // border
        "border-transparent",
        // background color
        "bg-red-600 dark:bg-red-700",
        // hover color
        "hover:bg-red-700 dark:hover:bg-red-600",
        // disabled
        "disabled:bg-red-300 disabled:text-white",
        "disabled:dark:bg-red-950 disabled:dark:text-red-400"
      ],
      outline: [
        // border
        "border-gray-300 dark:border-gray-700",
        // text color
        "text-gray-700 dark:text-gray-200",
        // background color
        "bg-transparent",
        // hover color
        "hover:bg-gray-50 dark:hover:bg-gray-800",
        // disabled
        "disabled:bg-transparent disabled:text-gray-400",
        "disabled:dark:bg-transparent disabled:dark:text-gray-600"
      ],
      link: [
        // base
        "shadow-none",
        // border
        "border-transparent",
        // text color
        "text-indigo-600 dark:text-indigo-400",
        // background color
        "bg-transparent",
        // hover color
        "hover:text-indigo-700 dark:hover:text-indigo-300 hover:underline",
        // disabled
        "disabled:text-gray-400 dark:disabled:text-gray-600",
        "disabled:no-underline"
      ]
    }
  },
  defaultVariants: {
    variant: "primary"
  }
})

interface ButtonProps
  extends ComponentPropsWithoutRef<"button">,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  isLoading?: boolean
  loadingText?: string
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      asChild,
      isLoading = false,
      loadingText,
      className,
      disabled,
      variant,
      children,
      ...props
    }: ButtonProps,
    forwardedRef
  ) => {
    const Component = asChild ? Slot : "button"
    return (
      <Component
        ref={forwardedRef}
        className={cx(buttonVariants({ variant }), className)}
        disabled={disabled ?? isLoading}
        {...props}>
        {isLoading ? (
          <span className="pointer-events-none flex shrink-0 items-center justify-center gap-1.5">
            <RiLoader2Fill
              className="size-4 shrink-0 animate-spin"
              aria-hidden="true"
            />
            <span className="sr-only">{loadingText ?? "Loading"}</span>
            {loadingText ?? children}
          </span>
        ) : (
          children
        )}
      </Component>
    )
  }
)

Button.displayName = "Button"

export { Button, buttonVariants, type ButtonProps }
